/**
 * The trip's driver (ST-00198): the API calls the plan & dispatch page makes to pick and show
 * who drives a trip. Shapes: `types/lexistar-trip-drivers`. Server:
 * server/modules/lexistar/trips (GET /trips:resolveDriver) and server/modules/lexistar/drivers
 * (GET /drivers), mounted at /api/lexistar.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response BODY.
 *
 *   resolveTripDriver({ asset_id | registration, at? })  → ResolveTripDriverResponse
 *        the driver allocated to the truck at `at` (default now) — the grid's default driver
 *        for a row, and the recipient of a trip that names none
 *   listActiveDrivers()                                  → RegisterDriverRow[]
 *        every active driver the caller may see (GET /drivers, all pages), for the picker
 */
import api from './api';
import type { RegisterDriverRow, ResolveTripDriverResponse } from '../types/lexistar-trip-drivers';

export type { RegisterDriverRow, ResolveTripDriverResponse };

const LX = '/lexistar';

/** GET /drivers's page maximum. */
const DRIVER_PAGE = 500;
/** At most this many pages (10 000 drivers) are read for the picker. */
const MAX_DRIVER_PAGES = 20;

export interface ResolveTripDriverParams {
  asset_id?: string;
  registration?: string;
  /** ISO instant (the row's planned start); omitted = now. */
  at?: string;
}

/** The driver allocated to a truck at an instant (lexistar.trips.view). */
export async function resolveTripDriver(
  params: ResolveTripDriverParams
): Promise<ResolveTripDriverResponse> {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const res = await api.get(`${LX}/trips:resolveDriver`, { params: query });
  return res.data as ResolveTripDriverResponse;
}

interface DriversPage {
  drivers?: RegisterDriverRow[];
  pagination?: { page?: number; pages?: number };
}

/** Every active driver the caller may see, all pages (lexistar.drivers.view or .manage). */
export async function listActiveDrivers(): Promise<RegisterDriverRow[]> {
  const out: RegisterDriverRow[] = [];
  for (let page = 1; page <= MAX_DRIVER_PAGES; page += 1) {
    const res = await api.get(`${LX}/drivers`, {
      params: { active: true, limit: DRIVER_PAGE, page },
    });
    const body = (res.data || {}) as DriversPage;
    out.push(...(body.drivers || []));
    if (page >= (body.pagination?.pages ?? 1)) break;
  }
  return out;
}
